/**
 * Centralized Application Route Definitions & Type-Safe Path Builders
 * Industry-standard lowercase, kebab-case, RESTful hierarchy.
 */

export const ROUTES = {
  // 1. Core & Public
  HOME: "/",
  SEARCH: "/search",
  LANGUAGE: "/language",
  PRIVACY_POLICY: "/privacy-policy",
  DOWNLOAD: "/download",

  // 2. Auth & Onboarding
  AUTH: {
    LOGIN: "/login",
    REGISTER: "/register",
    FORGOT_PASSWORD: "/forgot-password",
    ADD_PHONE: "/add-phone",
    ADD_PASSWORD: "/add-password",
    ATOM: "/auth/atom",
  },

  // 3. Catalog, Media & Player
  ARTISTS: "/artists",
  ARTIST_DETAIL: (id: any = ":id") => `/artists/${id}`,
  MUSIC: "/music",
  PLAYLISTS: "/playlists",
  PLAYLIST_DETAIL: (id: any = ":id") => `/playlists/${id}`,
  ALBUMS: "/albums",
  ALBUM_DETAIL: (id: any = ":id") => `/albums/${id}`,
  VIDEOS: "/videos",
  VIDEO_DETAIL: (id: any = ":id") => `/videos/${id}`,
  VIDEO_COMMENTS: (id: any = ":id", movieTitle?: string) =>
    `/videos/${id}/comments${movieTitle ? `?movieTitle=${encodeURIComponent(movieTitle)}` : ""}`,
  RADIO: "/radio",
  RADIO_STATION: (id: any = ":id") => `/radio/${id}`,
  QUEUE: "/queue",
  PLAYER: "/player",
  VOICE_TEST: "/voice-test",

  // 4. Ringback Tones (CRBT)
  TUNES: "/tunes",
  TUNE_PREVIEW: (id: any = ":id") => `/tunes/${id}/preview`,
  TUNE_BUY: (id: any = ":id") => `/tunes/${id}/buy`,

  // 5. Playlists Flow & Library
  LIBRARY: "/library",
  AUDIO_LIBRARY: "/audio-library",
  PLAYLISTS_CREATE: "/playlists/create",
  PLAYLISTS_CREATE_BROWSE_PATH: "/playlists/create/browse",
  PLAYLISTS_CREATE_BROWSE: (playlistid?: any) =>
    playlistid ? `/playlists/create/browse?playlistid=${playlistid}` : "/playlists/create/browse",
  PLAYLISTS_CREATE_ADD_PATH: "/playlists/create/add",
  PLAYLISTS_CREATE_ADD: (params: {
    playlistid: any;
    search?: string;
    id?: any;
    type?: string;
    category?: string;
  }) => {
    const sp = new URLSearchParams();
    sp.set("playlistid", String(params.playlistid));
    if (params.search) sp.set("search", params.search);
    if (params.id) sp.set("id", String(params.id));
    if (params.type) sp.set("type", params.type);
    if (params.category) sp.set("category", params.category);
    return `/playlists/create/add?${sp.toString()}`;
  },
  PLAYLISTS_CREATE_CONFIRM_PATH: "/playlists/create/confirm",
  PLAYLISTS_CREATE_CONFIRM: (playlistid?: any) =>
    playlistid ? `/playlists/create/confirm?playlistid=${playlistid}` : "/playlists/create/confirm",
  UPLOAD_SONGS: "/upload-songs",
  UPLOADED_SONGS: "/uploaded-songs",
  UPLOAD_PROGRESS: "/upload-progress",

  // 6. User Profile, Menu & Settings
  MENU: "/menu",
  MENU_TAB: (tab: string) => `/menu?tab=${encodeURIComponent(tab)}`,
  PROFILE: "/profile",
  PROFILE_EDIT: "/profile/edit",
  NOTIFICATIONS: "/notifications",
  SETTINGS: {
    SECURITY: "/settings/security",
    PASSWORD: "/settings/password",
    EMAIL: "/settings/email",
    PHONE: "/settings/phone",
    LOGIN_HISTORY: "/settings/login-history",
    ARTIST_USAGE: "/settings/artist-usage",
    NOTIFICATIONS: "/settings/notifications",
  },

  // 7. Subscriptions & Monetization
  SUBSCRIPTIONS: {
    ROOT: "/subscriptions",
    WITH_PACKAGE: (packageId: any) => `/subscriptions?packageId=${encodeURIComponent(String(packageId))}`,
    ATOM_PATH: "/subscriptions/atom",
    ATOM: (params?: {
      productCode?: string;
      clientTransactionId?: string;
      audiolibrary_id?: string;
      checkout?: boolean;
    }) => {
      if (!params) return "/subscriptions/atom";
      const sp = new URLSearchParams();
      if (params.productCode) sp.set("productCode", params.productCode);
      if (params.clientTransactionId) sp.set("clientTransactionId", params.clientTransactionId);
      if (params.audiolibrary_id) sp.set("audiolibrary_id", params.audiolibrary_id);
      if (params.checkout) sp.set("checkout", "1");
      const q = sp.toString();
      return q ? `/subscriptions/atom?${q}` : "/subscriptions/atom";
    },
    AYA_PAY_PATH: "/subscriptions/aya-pay",
    AYA_PAY: (params?: { productCode?: string; price?: string }) => {
      if (!params) return "/subscriptions/aya-pay";
      const sp = new URLSearchParams();
      if (params.productCode) sp.set("productCode", params.productCode);
      if (params.price) sp.set("price", params.price);
      const q = sp.toString();
      return q ? `/subscriptions/aya-pay?${q}` : "/subscriptions/aya-pay";
    },
    CRBT_UNSUBSCRIBE_PATH: "/subscriptions/crbt/unsubscribe",
    CRBT_UNSUBSCRIBE: (params?: {
      productCode: string;
      msisdn: string;
      tuneName?: string;
      actionType?: "delete-subscription" | "delete-tone" | string;
    }) => {
      if (!params) return "/subscriptions/crbt/unsubscribe";
      const sp = new URLSearchParams();
      sp.set("productCode", params.productCode);
      sp.set("msisdn", params.msisdn);
      if (params.tuneName) sp.set("tuneName", params.tuneName);
      if (params.actionType) sp.set("actionType", params.actionType);
      return `/subscriptions/crbt/unsubscribe?${sp.toString()}`;
    },
  },
} as const;
